import { ensureGeofences } from "../../../../../../services/useOrgData";
import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack } from '@mui/material';
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { mkConfig, generateCsv, download } from "export-to-csv";
import Topbar from "../../../../../../component/TopBar";
import DataTable from "../../../../../../component/DataTable";
import { attendanceDayColumns } from "../dayColumns";
import MainBox from "../../../../container/MainBox";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import { SearchInput, FilterSelect } from "../../../../../../component/listing";
import { getAllDaySelfAttendance } from "../../../../../../services/facialRecognition";

import { isPointInFence } from "../../../../../../utils/isPointInFence";
import { getDuration } from "../../../../../../utils/getDuration";
import { calculateTime } from "../../../../../../utils/calculateTime";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens } from "../../../../../../theme";

const statusOf = (row) => {
  if (row?.arrivalTime == null) return "Absent";
  if (row?.hasArrivedLate) return "Late";
  return "Present";
};
const STATUS_TONE = { Present: "success", Late: "warning", Absent: "danger" };

export default function MyAttendance() {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState([]);
  const [fence, setFence] = useState();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchAttendance();
    ensureGeofences().then((f) => setFence(f?.[0]?.geofenceObj?.fences?.[0])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAttendance = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getAllDaySelfAttendance();
      if (res?.success === false) throw new Error(res?.message || "Failed to load attendance");
      setData(Array.isArray(res?.data) ? [...res.data].reverse() : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load attendance");
    } finally {
      setLoading(false);
    }
  };

  // Location needs both a configured geofence and recorded coordinates;
  // otherwise it's unknown rather than "Remote".
  const locationOf = (row) => {
    if (!fence || row?.arrivalLat == null || row?.arrivalLong == null) return "—";
    return isPointInFence(fence, row.arrivalLat, row.arrivalLong) ? "In Office" : "Remote";
  };
  // Clock-in time comes from the day record's arrivalTime (minutes since
  // midnight), matching clock-out — not the document's createdAt timestamp.
  const clockInOf = (row) => row?.arrivalTime != null ? calculateTime(row.arrivalTime) : "--";
  const clockOutOf = (row) => !isNaN(Number(row?.departureTime)) ? calculateTime(row.departureTime) : "--";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((row) => {
      const matchesSearch = !q || (row?.dayString || "").toLowerCase().includes(q);
      const matchesStatus = statusFilter === "all" || statusOf(row) === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, statusFilter]);

  const exportCsv = () => {
    const rows = filtered.map((row) => ({
      Date: row?.dayString || "",
      Status: statusOf(row),
      Location: locationOf(row),
      ClockIn: clockInOf(row),
      ClockOut: clockOutOf(row),
      BreakTime: getDuration(row?.breakHours || 0),
      TotalTime: getDuration(row?.totalHours || 0),
      EffectiveHrs: getDuration(row?.effectiveHours || 0),
    }));
    if (!rows.length) { notify.error("Nothing to export"); return; }
    const cfg = mkConfig({ useKeysAsHeaders: true, filename: "my-attendance" });
    download(cfg)(generateCsv(cfg)(rows));
    notify.success(`Exported ${rows.length} day(s)`);
  };

  return (
    <Box>
      <Topbar MainHeading={"My Attendance"} />

      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by date" minWidth="14rem" />
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              options={[{ value: "all", label: "All statuses" }, { value: "Present", label: "Present" }, { value: "Late", label: "Late" }, { value: "Absent", label: "Absent" }]} />
          </Stack>
          <BtnSecondary onClick={exportCsv} sx={{ borderRadius: "8px" }}>
            <FileDownloadOutlinedIcon sx={{ fontSize: "1.125rem", mr: 0.5 }} /> Export CSV
          </BtnSecondary>
        </Stack>

        <DataTable
          rows={filtered}
          loading={loading}
          error={error}
          onRetry={fetchAttendance}
          errorTitle="Couldn't load attendance"
          empty={{
            title: data.length === 0 ? "No attendance records yet" : "No matches",
            description: data.length === 0
              ? "Your daily attendance will appear here once you clock in."
              : "Try a different search or filter.",
          }}
          getRowId={(row, i) => row._id || i}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={attendanceDayColumns({
            statusOf, statusTone: STATUS_TONE, locationOf, clockInOf, clockOutOf, getDuration,
          })}
        />
      </MainBox>
    </Box>
  );
}
